import { createContext, useContext } from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";

import { loadFlows } from "src/api";
import {
  agentListQueryOptions,
  isSettledAgentReadFailure,
} from "src/api/agents/management";
import type { Flow } from "src/api/flowTypes";
import { flowKeys } from "src/api/queries";
import type { Agent } from "src/clients/agent-management";
import { useCapabilities } from "src/hooks/useCapabilities";
import { ANY_FOLDER } from "src/hooks/useCapabilities/types";
import { useIsAgentsModuleEnabled } from "src/hooks/useIsAgentsModuleEnabled";
import { shouldShowInternalResources } from "src/router/featureFlags";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import type { ReferenceContextualEntity } from "src/unifiedCopilot/context/types";

export const GLOBAL_CONTEXTUAL_ENTITY_LIMIT = 100;

const GLOBAL_CONTEXTUAL_ENTITY_STALE_TIME = 1000 * 60 * 5;
const GLOBAL_FLOW_TYPES = ["none", "case_flow"];

export type GlobalContextualEntitiesContextValue = {
  entities: ReferenceContextualEntity[];
  refresh: () => Promise<void>;
};

/**
 * Global contextual entities are fetched when Unified Copilot mounts but
 * consumed deeper in the floating chat UI. Keeping the query-derived value in
 * a separate context avoids both prop-drilling through the window
 * infrastructure and synchronizing React Query data into the ambient-entity
 * Zustand store. It also lets the prompt request a stale-aware refresh without
 * owning the queries.
 */
export const GlobalContextualEntitiesContext =
  createContext<GlobalContextualEntitiesContextValue>({
    entities: [],
    refresh: () => Promise.resolve(),
  });

export const useGlobalContextualEntitiesContext =
  (): GlobalContextualEntitiesContextValue =>
    useContext(GlobalContextualEntitiesContext);

export type GlobalContextualEntityQueryScope = {
  workspaceId: string;
  workspaceBaseUrl?: string;
  includeInternalFlows: boolean;
  canAccessFlows: boolean;
  isAgentsModuleEnabled: boolean;
  canAccessAgents: boolean;
};

export const mapFlowsToContextualEntities = (
  flows: Flow[],
): ReferenceContextualEntity[] =>
  flows.map((flow) => ({
    kind: "reference",
    type: "flow",
    id: flow.id,
    displayName: flow.name,
  }));

export const mapAgentsToContextualEntities = (
  agents: Agent[],
): ReferenceContextualEntity[] =>
  agents.map((agent) => ({
    kind: "reference",
    type: "agent",
    id: agent.id,
    displayName: agent.name,
  }));

/**
 * The workspace flows behind the global entity catalogue.
 */
export const globalContextualEntityFlowsQueryOptions = ({
  workspaceId,
  includeInternalFlows,
}: {
  workspaceId: string;
  includeInternalFlows: boolean;
}) => ({
  queryKey: [
    ...flowKeys.all,
    "global-contextual-entities",
    workspaceId,
    {
      includedTypes: GLOBAL_FLOW_TYPES,
      includeInternalFlows,
      limit: GLOBAL_CONTEXTUAL_ENTITY_LIMIT,
    },
  ],
  queryFn: () =>
    loadFlows({
      workspaceId,
      skip: 0,
      limit: GLOBAL_CONTEXTUAL_ENTITY_LIMIT,
      include_internal_flows: includeInternalFlows,
      includedTypes: GLOBAL_FLOW_TYPES,
      include_schemas: false,
    }),
  staleTime: GLOBAL_CONTEXTUAL_ENTITY_STALE_TIME,
  throwOnError: false,
});

export const useGlobalContextualEntityQueries = (
  scope: GlobalContextualEntityQueryScope,
): GlobalContextualEntitiesContextValue => {
  const queryClient = useQueryClient();
  const canFetchFlows = Boolean(scope.workspaceId) && scope.canAccessFlows;
  const flowQueryOptions = {
    ...globalContextualEntityFlowsQueryOptions({
      workspaceId: scope.workspaceId,
      includeInternalFlows: scope.includeInternalFlows,
    }),
    enabled: canFetchFlows,
  };
  const { data: flows = [] } = useQuery(flowQueryOptions);
  const canFetchAgents =
    Boolean(scope.workspaceId) &&
    Boolean(scope.workspaceBaseUrl) &&
    scope.isAgentsModuleEnabled &&
    scope.canAccessAgents;
  const agentQueryOptions = {
    ...agentListQueryOptions({
      workspaceId: scope.workspaceId,
      workspaceBaseUrl: scope.workspaceBaseUrl,
      enabled: canFetchAgents,
    }),
    staleTime: GLOBAL_CONTEXTUAL_ENTITY_STALE_TIME,
    throwOnError: false,
  };
  const { data: agents = [], error: agentsError } = useQuery(agentQueryOptions);
  // The composer refreshes on every focus. A denied agent read never turns
  // into a grant while the session lives, so refetching it just repeats the
  // rejection - the client-side capability and the token can disagree.
  const canRefreshAgents =
    canFetchAgents && !isSettledAgentReadFailure(agentsError);

  const refresh = async (): Promise<void> => {
    const refreshes = [
      ...(canFetchFlows ? [queryClient.fetchQuery(flowQueryOptions)] : []),
      ...(canRefreshAgents ? [queryClient.fetchQuery(agentQueryOptions)] : []),
    ];

    await Promise.allSettled(refreshes);
  };

  return {
    entities: [
      ...(canFetchFlows
        ? mapFlowsToContextualEntities(
            flows.slice(0, GLOBAL_CONTEXTUAL_ENTITY_LIMIT),
          )
        : []),
      ...(canFetchAgents
        ? mapAgentsToContextualEntities(
            agents.slice(0, GLOBAL_CONTEXTUAL_ENTITY_LIMIT),
          )
        : []),
    ],
    refresh,
  };
};

export const useWorkspaceGlobalContextualEntities =
  (): GlobalContextualEntitiesContextValue => {
    const { workspace } = useWorkspaceContext();
    const capabilities = useCapabilities();
    const { isEnabled: isAgentsModuleEnabled } = useIsAgentsModuleEnabled();

    return useGlobalContextualEntityQueries({
      workspaceId: workspace.id,
      workspaceBaseUrl: workspace.base_url,
      includeInternalFlows: shouldShowInternalResources(),
      canAccessFlows:
        !capabilities.isLoading && capabilities.flows.canAccess(ANY_FOLDER),
      isAgentsModuleEnabled,
      canAccessAgents: !capabilities.isLoading && capabilities.agents.canAccess,
    });
  };
